import { ComponentElementProperty } from "../component_element/ComponentElementManifest.model";
import { FormDataSchema, FormField, FormSchemaAlias } from "./FormDataSchema.model";
import { DocumentationDescription } from "../model/Documentation.model";
import { DataType } from "../model/DataType.model";


/**
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface FormContent {
    applicationId?: string;
    formId?: string;
    name?: string;
    pageId?: string;
    version?: string;
    form: FormContentForm;
    formSchema?: FormDataSchema;
    fragment?: {
        [key: string]: FormContent
    };
    callback?: FormCallback;
    extend?: {
       css?: string;
       script?: string;
    };
    linkedBlock?: {
        [key: string]: string;
    };
    linkedForm?: {
        [key: string]: string;
    };
    linkedFormFlow?: {
        [key: string]: string;
    };
    config?: FormConfig;
}

/**
 * 폼 컨피그 정보
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface FormConfig {
    popupValidation?: boolean;
    landingScript?: string;
    landingPage?: string;
    step?: string;
    accessibility?: {
        modal?: boolean;
        popupValidation?: boolean;
        restoreFocus?: boolean;
    };
    session?: {
        ttl?: number;
        update?: {
            ajaxCall?: boolean;
            ajaxResponse?: boolean;
            dataChange?: boolean;
            stateChange?: boolean;
        };
        msg?: string;
    };
}

/**
 * 폼 컨텐트 정보
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface FormContentForm {
    [key: string]: FormContentInformation;
}

/**
 * 폼 컨텐트 정보
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface FormContentInformation {
    /** Link Data Schema Field */
    dataField?: string;
    /** 컴퍼넌트를 그릴 때 당시의 타입, 각 폼이나 프래그 먼트를 그릴 당시의 원본 타입 정보 -> 모델러에서 타입 변경 전 후로 그릴 수 있기 때문에 상호 다름을 확인 할 용도 */
    dataType?: DataType;
    /** Link Data Schema Field */
    dataSchema?: FormField;
    /** Link Data Grid id */
    gridId?: string;
    /** Render Information */
    componentLayout: string;
    layoutProperty?: LayoutProperty;
    labelWidth?: string;
    component: string;
    /** 부모가 bf-container 을 여럿 가지고 있을 경우 해당 타켓 지정 */
    container?: string;
    condition: FormContentCondition;
    /** Property */
    property: ComponentElementProperty;
    /** Child Nodes */
    childNodes: FormContentForm;
}

/**
 * 폼 앨리먼트 컨텐트 컨디션
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface FormContentCondition {
    data?: FormSchemaAlias;
    globalConditionInherit?: boolean;
    parentConditionInherit?: boolean;
    displayNone?: FormContentConditionContent | "true";
    visibilityHidden?: FormContentConditionContent | "true";
    editable?: FormContentConditionContent | "true";
    readonly?: FormContentConditionContent | "true";
    disabled?: FormContentConditionContent | "true";
    printable?: FormContentConditionContent | "true";
}

export interface FormContentConditionContent extends DocumentationDescription {
    condition?: string;
}

/**
 * 폼 앨리먼트 컨텐트 컨디션 이름
 * @since 0.0.1
 * @author Heungsoo Ham
 */
export enum FormContentConditionName {
    DISPLAYNONE = "displayNone",
    VISIBILITYHIDDEN = "visibilityHidden",
    EDITABLE = "editable",
    READONLY = "readonly",
    DISABLED = "disabled",
    PRINTABLE = "printable"
}

/**
 * 컴퍼넌트 앨리먼트 레이아웃 프로퍼티
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export const DefaultLabelWidth = "30%";
export const DefaultLayoutWidth = "100%";

export interface LayoutProperty {
    label?: string;
    width?: string;
    height?: string;
    labelWidth?: string;
    marginTop?: string;
    marginRight?: string;
    marginBottom?: string;
    marginLeft?: string;
    paddingTop?: string;
    paddingRight?: string;
    paddingBottom?: string;
    paddingLeft?: string;
}

// true  값 리턴은 값 취득 실패
export type PublishCallback = (formContent: FormContent | boolean, formSchema: FormDataSchema | boolean, ...args) => void;

/**
 * 폼 콜백 인터페이스
 * @since 0.0.1
 * @author Jongkwang Park
 * @interface
 */
export interface FormCallback {
    beforeData?: CallbackBaseProperty;
    afterData?: CallbackBaseProperty;
    beforeSave?: CallbackBaseProperty;
    afterSave?: CallbackBaseProperty;
    beforeExit?: CallbackBaseProperty;
    afterExit?: CallbackBaseProperty;
    dataChangeListener?: Array<CallbackProperty>;
    stateChangeListener?: Array<CallbackProperty>;
    modal?: {
        /* key is a modal component's elementId */
        [key: string]: {
            openCallback?: CallbackBaseProperty;
            applyCallback?: CallbackBaseProperty;
            closeCallback?: CallbackBaseProperty;
        }
    };
}

/**
 * 폼 콜백 베이스 속성 인터페이스
 * @since 0.0.1
 * @author Jongkwang Park
 * @interface
 */
export interface CallbackBaseProperty extends DocumentationDescription {
    data?: FormSchemaAlias;
    function?: string;
}

/**
 * 폼 콜백 속성 인터페이스
 * @since 0.0.1
 * @author Jongkwang Park
 * @interface
 */
export interface CallbackProperty extends CallbackBaseProperty {
    observable: FormSchemaAlias;
}
